import React, { useState, useEffect } from 'react';
import {
  FaFileContract, FaEdit, FaSearch, FaSignature, FaPaperPlane,
  FaCheckCircle, FaCheck, FaTimes
} from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';
import { generateForm130U } from '../contracts/texasForm130U';

const EContractManagerFixed = ({ dealData = {}, customerData = {}, vehicleData = {}, onContractComplete }) => {
  const [activeStep, setActiveStep] = useState('contracts');
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [emailAddress, setEmailAddress] = useState(customerData.email || '');
  const [isSending, setIsSending] = useState(false);
  const [contractTemplateData, setContractTemplateData] = useState(null);

  // Define steps for the contract wizard
  const steps = [
    { id: 'contracts', label: 'Select Contracts', icon: FaFileContract },
    { id: 'prepare', label: 'Prepare', icon: FaEdit },
    { id: 'review', label: 'Review', icon: FaSearch },
    { id: 'sign', label: 'Sign', icon: FaSignature },
    { id: 'send', label: 'Send', icon: FaPaperPlane },
    { id: 'complete', label: 'Complete', icon: FaCheckCircle }
  ];

  // Load deal data from localStorage if available
  useEffect(() => {
    const storedDealData = localStorage.getItem('currentDealData');
    const storedContractData = localStorage.getItem('contractTemplateData');

    if (storedContractData) {
      setContractTemplateData(JSON.parse(storedContractData));
    }

    if (storedDealData && !dealData.id) {
      const parsedDeal = JSON.parse(storedDealData);
      // Use stored deal data if no deal data was passed as prop
      dealData = parsedDeal;
      customerData = parsedDeal.customer || {};
      vehicleData = parsedDeal.vehicle || {};
      setEmailAddress(customerData.email || '');
    }

    // Listen for deal data updates
    const handleDealUpdate = (event) => {
      if (event.detail && event.detail.contractData) {
        setContractTemplateData(event.detail.contractData);
      }
    };

    window.addEventListener('dealDataUpdated', handleDealUpdate);
    return () => window.removeEventListener('dealDataUpdated', handleDealUpdate);
  }, []);

  const availableContracts = [
    { id: 'buyers-order', name: "Buyer's Order", required: true },
    { id: 'retail-installment', name: 'Retail Installment Contract', required: true },
    { id: 'form-130u', name: 'Form 130-U (Texas Title/Registration)', required: true },
    { id: 'privacy-notice', name: 'Privacy Notice', required: true },
    { id: 'buyers-guide', name: 'FTC Buyers Guide', required: true },
    { id: 'odometer-disclosure', name: 'Odometer Disclosure', required: true },
    { id: 'insurance-verification', name: 'Insurance Verification', required: false },
    { id: 'warranty-disclaimer', name: 'Warranty Disclaimer', required: false },
    { id: 'gap-agreement', name: 'GAP Agreement', required: false },
    { id: 'service-contract', name: 'Service Contract', required: false }
  ];

  const handleContractToggle = (contractId) => {
    setSelectedContracts(prev =>
      prev.includes(contractId)
        ? prev.filter(id => id !== contractId)
        : [...prev, contractId]
    );
  };

  const handlePreviewContract = (contract) => {
    if (!contract) return;

    // Get the specific contract data if available
    const templateData = contractTemplateData?.[contract.id.replace(/-/g, '')] || {};

    // Generate contract-specific content based on type
    let contractContent = '';

    if (contract.id === 'buyers-order' && contractTemplateData?.buyersOrder) {
      const data = contractTemplateData.buyersOrder;
      contractContent = `
        <div class="section">
          <h2>Deal Information</h2>
          <p><strong>Deal Number:</strong> ${data.dealNumber}</p>
          <p><strong>Date:</strong> ${data.date}</p>
        </div>
        
        <div class="section">
          <h2>Buyer Information</h2>
          <p><strong>Name:</strong> ${data.buyer}</p>
          <p><strong>Address:</strong> ${data.buyerAddress}</p>
          <p><strong>Phone:</strong> ${data.buyerPhone}</p>
          <p><strong>Email:</strong> ${data.buyerEmail}</p>
        </div>
        
        <div class="section">
          <h2>Vehicle Information</h2>
          <p><strong>Vehicle:</strong> ${data.vehicle}</p>
          <p><strong>VIN:</strong> ${data.vin}</p>
          <p><strong>Stock #:</strong> ${data.stockNumber}</p>
          <p><strong>Mileage:</strong> ${data.mileage?.toLocaleString()} miles</p>
        </div>
        
        <div class="section">
          <h2>Financial Details</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr><td style="padding: 5px;">Sale Price:</td><td style="text-align: right;">$${parseFloat(data.salePrice || 0).toLocaleString()}</td></tr>
            <tr><td style="padding: 5px;">Trade-In Allowance:</td><td style="text-align: right;">-$${parseFloat(data.tradeInAllowance || 0).toLocaleString()}</td></tr>
            <tr><td style="padding: 5px; border-top: 1px solid #999;">Subtotal:</td><td style="text-align: right; border-top: 1px solid #999;">$${parseFloat(data.subtotal || 0).toLocaleString()}</td></tr>
            <tr><td style="padding: 5px;">Sales Tax:</td><td style="text-align: right;">$${parseFloat(data.salesTax || 0).toLocaleString()}</td></tr>
            <tr><td style="padding: 5px;">Doc Fee:</td><td style="text-align: right;">$${parseFloat(data.docFee || 0).toLocaleString()}</td></tr>
            <tr><td style="padding: 5px;">License Fee:</td><td style="text-align: right;">$${parseFloat(data.licenseFee || 0).toLocaleString()}</td></tr>
            <tr><td style="padding: 5px;">Other Fees:</td><td style="text-align: right;">$${parseFloat(data.otherFees || 0).toLocaleString()}</td></tr>
            <tr><td style="padding: 5px; border-top: 2px solid #333; font-weight: bold;">Total Price:</td><td style="text-align: right; border-top: 2px solid #333; font-weight: bold;">$${parseFloat(data.totalPrice || 0).toLocaleString()}</td></tr>
          </table>
        </div>
        
        ${data.financingType !== 'cash' ? `
        <div class="section">
          <h2>Financing Terms</h2>
          <p><strong>Down Payment:</strong> $${parseFloat(data.downPayment || 0).toLocaleString()}</p>
          <p><strong>Amount Financed:</strong> $${parseFloat(data.amountFinanced || 0).toLocaleString()}</p>
          <p><strong>APR:</strong> ${data.apr}%</p>
          <p><strong>Term:</strong> ${data.term} months</p>
          <p><strong>Monthly Payment:</strong> $${parseFloat(data.monthlyPayment || 0).toFixed(2)}</p>
        </div>
        ` : ''}
      `;
    } else if (contract.id === 'form-130u' || contract.id === 'title-application') {
      // Use the Texas Form 130-U generator
      contractContent = generateForm130U(dealData, customerData, vehicleData);
    } else if (contract.id === 'privacy-notice') {
      // ACTUAL PRIVACY NOTICE CONTENT
      contractContent = `
        <div class="section">
          <h2>FACTS</h2>
          <h3>WHAT DOES PARADISE-PERFORMANCE DO WITH YOUR PERSONAL INFORMATION?</h3>
        </div>
        
        <div class="section">
          <h3>Why?</h3>
          <p>Financial companies choose how they share your personal information. Federal law gives consumers the right to limit some but not all sharing. Federal law also requires us to tell you how we collect, share, and protect your personal information. Please read this notice carefully to understand what we do.</p>
        </div>
        
        <div class="section">
          <h3>What?</h3>
          <p>The types of personal information we collect and share depend on the product or service you have with us. This information can include:</p>
          <ul style="margin-left: 20px; list-style-type: disc;">
            <li>Social Security number and income</li>
            <li>Account balances and payment history</li>
            <li>Credit history and credit scores</li>
          </ul>
        </div>
        
        <div class="section">
          <h3>How?</h3>
          <p>All financial companies need to share customers' personal information to run their everyday business. In the section below, we list the reasons financial companies can share their customers' personal information; the reasons Paradise-Performance chooses to share; and whether you can limit this sharing.</p>
        </div>
        
        <div class="section">
          <h3>Reasons we can share your personal information</h3>
          <table style="width: 100%; border: 1px solid #ddd; margin-top: 10px;">
            <tr style="background: #f5f5f5;">
              <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Reasons we can share</th>
              <th style="padding: 8px; text-align: center; border: 1px solid #ddd;">Does Paradise-Performance share?</th>
              <th style="padding: 8px; text-align: center; border: 1px solid #ddd;">Can you limit this sharing?</th>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">For our everyday business purposes - such as to process your transactions, maintain your account(s), respond to court orders and legal investigations, or report to credit bureaus</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;"><strong>YES</strong></td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">NO</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">For our marketing purposes - to offer our products and services to you</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;"><strong>YES</strong></td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">NO</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">For joint marketing with other financial companies</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">NO</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">We don't share</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">For our affiliates' everyday business purposes - information about your transactions and experiences</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">NO</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">We don't share</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">For our affiliates' everyday business purposes - information about your creditworthiness</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">NO</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">We don't share</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">For nonaffiliates to market to you</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">NO</td>
              <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">We don't share</td>
            </tr>
          </table>
        </div>
        
        <div class="section">
          <h3>Questions?</h3>
          <p>Call (817) 382-2316 or email sales@paradise-performance.com</p>
        </div>
        
        <div class="section">
          <h3>Who we are</h3>
          <p><strong>Who is providing this notice?</strong><br>
          Paradise-Performance<br>
          305 Co Rd 3470<br>
          Paradise, TX 76073</p>
        </div>
        
        <div class="section">
          <h3>What we do</h3>
          <p><strong>How does Paradise-Performance protect my personal information?</strong><br>
          To protect your personal information from unauthorized access and use, we use security measures that comply with federal law. These measures include computer safeguards and secured files and buildings.</p>
          
          <p><strong>How does Paradise-Performance collect my personal information?</strong><br>
          We collect your personal information, for example, when you:</p>
          <ul style="margin-left: 20px; list-style-type: disc;">
            <li>Apply for financing or apply for a lease</li>
            <li>Provide account information or give us your contact information</li>
            <li>Show us your driver's license</li>
          </ul>
          
          <p><strong>Why can't I limit all sharing?</strong><br>
          Federal law gives you the right to limit only:</p>
          <ul style="margin-left: 20px; list-style-type: disc;">
            <li>Sharing for affiliates' everyday business purposes - information about your creditworthiness</li>
            <li>Affiliates from using your information to market to you</li>
            <li>Sharing for nonaffiliates to market to you</li>
          </ul>
          <p>State laws and individual companies may give you additional rights to limit sharing.</p>
        </div>
      `;
    } else {
      // Default content for other contracts
      contractContent = `
        <div class="section">
          <h2>Vehicle Information</h2>
          <p><strong>Vehicle:</strong> ${vehicleData?.year || ''} ${vehicleData?.make || ''} ${vehicleData?.model || ''}</p>
          <p><strong>VIN:</strong> ${vehicleData?.vin || 'N/A'}</p>
          <p><strong>Stock #:</strong> ${vehicleData?.stockNumber || 'N/A'}</p>
          <p><strong>Price:</strong> $${vehicleData?.price?.toLocaleString() || '0'}</p>
        </div>
        
        <div class="section">
          <h2>Customer Information</h2>
          <p><strong>Name:</strong> ${customerData?.firstName || ''} ${customerData?.lastName || ''}</p>
          <p><strong>Email:</strong> ${customerData?.email || 'N/A'}</p>
          <p><strong>Phone:</strong> ${customerData?.phone || 'N/A'}</p>
          <p><strong>Address:</strong> ${customerData?.address || 'N/A'}</p>
        </div>
      `;
    }

    // Create a preview window with the contract details
    const previewWindow = window.open('', '_blank', 'width=800,height=600');
    if (previewWindow) {
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${contract.name} - Preview</title>
            <style>
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                padding: 40px; 
                max-width: 800px; 
                margin: 0 auto;
                color: #333;
                line-height: 1.6;
              }
              h1 { color: #1a73e8; margin-bottom: 20px; }
              h2 { color: #555; margin-top: 30px; }
              .header { border-bottom: 2px solid #1a73e8; padding-bottom: 20px; margin-bottom: 30px; }
              .section { margin: 20px 0; padding: 15px; background: #f5f5f5; border-radius: 5px; }
              .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; }
              table { width: 100%; }
              @media print { 
                body { padding: 20px; }
                .footer { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${contract.name}</h1>
              <p>Deal #${dealData?.dealNumber || dealData?.id || 'DL001'} | Generated: ${new Date().toLocaleString()}</p>
            </div>
            
            ${contractContent}
            
            <div class="section">
              <h2>Signatures</h2>
              <div style="margin-top: 40px;">
                <div style="float: left; width: 45%;">
                  <div style="border-top: 1px solid #333; padding-top: 5px;">
                    <p><strong>Buyer Signature</strong></p>
                    <p>Date: _____________</p>
                  </div>
                </div>
                <div style="float: right; width: 45%;">
                  <div style="border-top: 1px solid #333; padding-top: 5px;">
                    <p><strong>Dealer Representative</strong></p>
                    <p>Date: _____________</p>
                  </div>
                </div>
                <div style="clear: both;"></div>
              </div>
            </div>
            
            <div class="footer">
              <p>Paradise-Performance</p>
              <p>305 Co Rd 3470, Paradise, TX 76073 | (817) 382-2316</p>
              <p>sales@paradise-performance.com | paradise-performance.com</p>
            </div>
          </body>
        </html>
      `);
      previewWindow.document.close();
    }
  };

  const handleContinue = () => {
    const currentIndex = steps.findIndex(s => s.id === activeStep);
    if (currentIndex < steps.length - 1) {
      setActiveStep(steps[currentIndex + 1].id);
    }
  };

  return (
    <div className="bg-white rounded-lg">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-colors ${step.id === activeStep ? 'bg-primary-600 text-white' :
                    steps.findIndex(s => s.id === activeStep) > index ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                  onClick={() => setActiveStep(step.id)}>
                  <step.icon className="w-5 h-5" />
                </div>
                <p className={`text-xs mt-2 ${step.id === activeStep ? 'text-primary-600 font-medium' : 'text-gray-600'}`}>
                  {step.label}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${steps.findIndex(s => s.id === activeStep) > index ? 'bg-green-600' : 'bg-gray-200'
                  }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      {activeStep === 'contracts' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Select Contracts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableContracts.map(contract => (
              <div
                key={contract.id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${selectedContracts.includes(contract.id)
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
                onClick={() => handleContractToggle(contract.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{contract.name}</p>
                    {contract.required && (
                      <span className="text-xs text-red-600">Required</span>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedContracts.includes(contract.id)
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-gray-300'
                    }`}>
                    {selectedContracts.includes(contract.id) && (
                      <FaCheck className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeStep === 'info' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Fill Information</h3>
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Document generation is ready for configuration. Connect your document generation service to complete this step.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Odometer Reading</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Current mileage"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Temp Tag Number</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Tag number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Representative</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Sales rep name"
              />
            </div>
          </div>
        </div>
      )}

      {activeStep === 'review' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Review Documents</h3>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Review all contract documents before proceeding. Make sure all information is accurate.
            </p>
          </div>
          <div className="space-y-2">
            {selectedContracts.map(contractId => {
              const contract = availableContracts.find(c => c.id === contractId);
              return (
                <div key={contractId} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                  <span className="font-medium">{contract?.name}</span>
                  <button
                    onClick={() => handlePreviewContract(contract)}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    Preview
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeStep === 'sign' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Sign Documents</h3>
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-700">
              Dealer signature is required before sending documents to the customer.
            </p>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <FaSignature className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Click here to add dealer signature</p>
          </div>
        </div>
      )}

      {activeStep === 'send' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Send to Customer</h3>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              Documents are ready to be sent to the customer for e-signature.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
              value={dealData?.customer?.email || ''}
              placeholder="customer@email.com"
            />
          </div>
        </div>
      )}

      {activeStep === 'complete' && (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Contracts Sent Successfully!</h3>
          <p className="text-gray-600">
            {selectedContracts.length} contracts have been sent to the customer.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={() => {
            const currentIndex = steps.findIndex(s => s.id === activeStep);
            if (currentIndex > 0) {
              setActiveStep(steps[currentIndex - 1].id);
            }
          }}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          disabled={activeStep === 'contracts'}
        >
          Previous
        </button>

        {activeStep !== 'complete' ? (
          <button
            onClick={handleContinue}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Continue
          </button>
        ) : (
          <button
            onClick={() => onContractComplete && onContractComplete(selectedContracts)}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Finish
          </button>
        )}
      </div>
    </div>
  );
};

export default EContractManagerFixed;
